import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { TrendingDown, Calculator, Award, ArrowRight } from 'lucide-react';

const RatgeberWertminderungPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Wertminderung nach Unfall: Anspruch & Berechnung | Ratgeber</title>
        <meta name="description" content="Wertminderung nach Unfallschaden: Was ist das? Wer zahlt? Wie wird berechnet? Wie hoch kann sie sein? Alle Infos + Beispielrechnungen." />
        <link rel="canonical" href="https://www.dein-gutachten-direkt.de/ratgeber/wertminderung" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Wertminderung nach Unfallschaden: Ihr Anspruch",
            "author": {"@type": "Organization", "name": "Dein Gutachten Direkt", "url": "https://www.dein-gutachten-direkt.de"},
            "publisher": {
              "@type": "Organization", 
              "name": "Dein Gutachten Direkt",
              "logo": {
                "@type": "ImageObject",
                "url": "https://customer-assets.emergentagent.com/job_gutachten-preview/artifacts/gusc9edr_image.png",
                "width": 200,
                "height": 60
              }
            },
            "image": {
              "@type": "ImageObject",
              "url": "https://customer-assets.emergentagent.com/job_gutachten-preview/artifacts/gusc9edr_image.png",
              "width": 1200,
              "height": 630,
              "caption": "Wertminderung nach Unfallschaden",
              "creditText": "Dein Gutachten Direkt",
              "creator": {"@type": "Organization", "name": "Dein Gutachten Direkt"},
              "copyrightNotice": "© 2026 Dein Gutachten Direkt"
            },
            "datePublished": "2026-01-20",
            "dateModified": "2026-02-05",
            "description": "Wertminderung nach Unfallschaden: Was ist das? Wer zahlt? Wie wird berechnet?"
          })}
        </script>
      </Helmet>
      <Header />
      <main className="pt-24 pb-16">
        <article className="max-w-4xl mx-auto px-6">
          {/* Author & Date Info */}
          <div className="mb-8 pb-6 border-b border-gray-200">
            <span className="text-brand-green font-semibold text-sm">Ratgeber</span>
            <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-brand-green/10 rounded-full flex items-center justify-center">
                  <Award className="w-4 h-4 text-brand-green" />
                </div>
                <span>Geschrieben von: <strong>Dein Gutachten Direkt Team</strong></span>
              </div>
              <span className="hidden sm:inline">•</span>
              <span>Veröffentlicht: <strong>20. Januar 2026</strong></span>
              <span className="hidden sm:inline">•</span>
              <span>Zuletzt aktualisiert: <strong>5. Februar 2026</strong></span>
            </div>
            <p className="text-xs text-gray-500 mt-2">Von erfahrenen Kfz-Sachverständigen</p>
          </div>
          
          <div className="mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mt-2 mb-4">Wertminderung nach Unfallschaden</h1>
            <p className="text-xl text-gray-600">Warum Ihr Auto nach einem Unfall weniger wert ist und wie Sie diesen Anspruch geltend machen</p>
          </div>
          
          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Was ist Wertminderung?</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Auch nach fachgerechter Reparatur verliert ein Unfallfahrzeug an Wert. Käufer zahlen weniger, weil das Fahrzeug „unfallgeschädigt“ ist – auch wenn alle Schäden behoben wurden. Diese <strong>merkantile Wertminderung</strong> ist ein eigenständiger Schadensposten, den die gegnerische Versicherung zahlen muss.
            </p>

            <div className="bg-brand-green/10 border-2 border-brand-green/30 rounded-2xl p-6 my-8">
              <div className="flex items-start gap-4">
                <TrendingDown className="w-8 h-8 text-brand-green flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Beispiel: VW Golf, 2 Jahre alt</h3>
                  <p className="text-gray-700 text-sm mb-3">Unfallschaden: 5.000 € Reparaturkosten</p>
                  <p className="text-gray-700 text-sm"><strong>Wertminderung:</strong> ca. 1.200 - 1.800 € zusätzlich!</p>
                  <p className="text-gray-600 text-xs mt-2">Das ist Geld, das Ihnen zusteht – zusätzlich zur Reparatur.</p>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-4">Wann besteht Anspruch auf Wertminderung?</h2>
            <p className="text-gray-700 mb-4">Voraussetzungen:</p>
            <ul className="space-y-2 text-gray-700 mb-6">
              <li>✓ <strong>Unverschuldeter Unfall</strong> (Sie sind nicht schuld)</li>
              <li>✓ <strong>Fahrzeug nicht zu alt</strong> (meist bis 5 Jahre, Einzelfall entscheidet)</li>
              <li>✓ <strong>Nicht zu viele Kilometer</strong> (Richtwert: unter 100.000 km)</li>
              <li>✓ <strong>Reparatur wurde durchgeführt</strong> (merkantile Wertminderung)</li>
              <li>✓ <strong>Dokumentiert im Gutachten</strong> (Kostenvoranschlag reicht NICHT!)</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-4">Wie wird Wertminderung berechnet?</h2>
            <p className="text-gray-700 mb-6">Es gibt mehrere Berechnungsmethoden. Die bekanntesten:</p>
            
            <div className="bg-gray-50 rounded-xl p-6 mb-6">
              <h4 className="font-bold text-gray-900 mb-3">1. Ruhkopf/Sahner-Methode (häufigste)</h4>
              <p className="text-gray-700 text-sm">Berücksichtigt: Fahrzeugalter, Laufleistung, Reparaturkosten, Vorschäden. Ergibt meist 10-25% der Reparaturkosten.</p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 mb-6">
              <h4 className="font-bold text-gray-900 mb-3">2. Halbgewachs-Methode</h4>
              <p className="text-gray-700 text-sm">Vereinfachte Berechnung, oft bei kleineren Schäden.</p>
            </div>

            <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">Faustformel:</h3>
            <div className="bg-blue-50 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <Calculator className="w-6 h-6 text-brand-blue" />
                <span className="font-bold text-gray-900">Wertminderung ≈ 10-25% der Reparaturkosten</span>
              </div>
              <p className="text-gray-700 text-sm">Abhängig von Alter, Laufleistung und Schadensumfang des Fahrzeugs.</p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-4">Wie mache ich Wertminderung geltend?</h2>
            <ol className="space-y-4 text-gray-700">
              <li><strong>1. Unabhängiges Gutachten erstellen lassen</strong> – nur im Gutachten wird Wertminderung berechnet.</li>
              <li><strong>2. Gutachten an gegnerische Versicherung senden</strong> – mit allen Schadensposten inklusive Wertminderung.</li>
              <li><strong>3. Wertminderung wird ausgezahlt</strong> – zusammen mit Reparaturkosten und Nutzungsausfall.</li>
            </ol>

            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 mt-8">
              <h4 className="font-bold text-yellow-900 mb-2">⚠️ Ohne Gutachten = Kein Geld</h4>
              <p className="text-gray-800 text-sm">Ein Werkstatt-Kostenvoranschlag enthält KEINE Wertminderung. Nur ein Sachverständigen-Gutachten berücksichtigt diesen Anspruch.</p>
            </div>

            {/* Internal Links Section */}
            <div className="bg-gray-50 rounded-2xl p-8 mt-12">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Weitere hilfreiche Ratgeber</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <Link to="/ratgeber/nach-unfall-checkliste" className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-200 hover:border-brand-green hover:shadow-md transition-all group">
                  <ArrowRight className="w-5 h-5 text-brand-green group-hover:translate-x-1 transition-transform" />
                  <div>
                    <span className="font-semibold text-gray-900 group-hover:text-brand-green transition-colors">Nach dem Unfall: Checkliste</span>
                    <p className="text-sm text-gray-600">Was Sie nach einem Unfall tun sollten</p>
                  </div>
                </Link>
                <Link to="/ratgeber/gutachten-oder-kostenvoranschlag" className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-200 hover:border-brand-green hover:shadow-md transition-all group">
                  <ArrowRight className="w-5 h-5 text-brand-green group-hover:translate-x-1 transition-transform" />
                  <div>
                    <span className="font-semibold text-gray-900 group-hover:text-brand-green transition-colors">Gutachten vs. Kostenvoranschlag</span>
                    <p className="text-sm text-gray-600">Was ist der Unterschied?</p>
                  </div>
                </Link>
              </div>
              
              <h4 className="font-bold text-gray-900 mt-8 mb-4">Gutachten in Ihrer Region</h4>
              <div className="flex flex-wrap gap-3">
                <Link to="/unfallgutachten-hamburg" className="text-sm px-4 py-2 bg-white rounded-lg border border-gray-200 hover:border-brand-green hover:text-brand-green transition-all">Hamburg</Link>
                <Link to="/unfallgutachten-norderstedt" className="text-sm px-4 py-2 bg-white rounded-lg border border-gray-200 hover:border-brand-green hover:text-brand-green transition-all">Norderstedt</Link>
                <Link to="/unfallgutachten-pinneberg" className="text-sm px-4 py-2 bg-white rounded-lg border border-gray-200 hover:border-brand-green hover:text-brand-green transition-all">Pinneberg</Link>
                <Link to="/unfallgutachten-mainz" className="text-sm px-4 py-2 bg-white rounded-lg border border-gray-200 hover:border-brand-green hover:text-brand-green transition-all">Mainz</Link>
                <Link to="/unfallgutachten-frankfurt" className="text-sm px-4 py-2 bg-white rounded-lg border border-gray-200 hover:border-brand-green hover:text-brand-green transition-all">Frankfurt</Link>
                <Link to="/unfallgutachten-wiesbaden" className="text-sm px-4 py-2 bg-white rounded-lg border border-gray-200 hover:border-brand-green hover:text-brand-green transition-all">Wiesbaden</Link>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default RatgeberWertminderungPage;